/**
 * What this device remembers about a player and voice: the privacy
 * acknowledgement, the age answer, and the voice preferences. See
 * docs/VOICE_CHAT.md, "Recording acknowledgement" and "Voice and age".
 *
 * Local copies only. The server keeps its own acknowledgement
 * (`policyAcks/{uid}`) and that is the one voice enforcement trusts; this one
 * decides what the UI offers before asking, and is what gets re-posted when a
 * sign-in lands on a different uid.
 */
import { browserStorage, readJson, type KeyValueStorage } from "./storage";

export const PRIVACY_KEY = "fiveout:privacy";
export const AGE_GATE_KEY = "fiveout:voice.ageGate";
const AUTO_JOIN_KEY = "fiveout:voice.autoJoin";
const UNMUTED_MATCHES_KEY = "fiveout:voice.unmutedMatches";
const LEVEL_KEY = "fiveout:voice.level";

/** The "You're muted" callout retires after a player has unmuted in this many matches. */
export const CALLOUT_RETIRES_AFTER = 3;

export type AgeBand = "under13" | "13-17" | "18+";
/** The bands the server stores. Under 13 is never sent anywhere. */
export type VoiceAgeBand = Exclude<AgeBand, "under13">;

/**
 * Which band a birth month and year put somebody in today.
 *
 * Only a month and year are asked, so the birthday itself is unknown; a birth
 * month equal to the current one counts as NOT yet had. That errs toward the
 * younger band, which is the side the age question exists to protect - a
 * player who turns 13 this month waits until next month for voice, rather than
 * a 12-year-old getting it a few weeks early.
 */
export function ageBandFromBirth(month: number, year: number, now: Date): AgeBand {
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(year)) {
    throw new RangeError(`not a birth month and year: ${month}/${year}`);
  }
  const thisMonth = now.getMonth() + 1;
  const age = now.getFullYear() - year - (month >= thisMonth ? 1 : 0);
  if (age < 13) return "under13";
  if (age < 18) return "13-17";
  return "18+";
}

/**
 * The age answer as the app acts on it: once a device has answered under 13,
 * every later answer is under 13 too.
 *
 * That is the half of the FTC's guidance on age screens a neutral question does
 * not cover - a child told "not available" who goes back and picks an earlier
 * year must get the same answer, or the question only teaches which year to
 * pick. Nothing about it goes to the server.
 */
export function resolveAgeAnswer(month: number, year: number, now: Date, storage: KeyValueStorage = browserStorage()): AgeBand {
  if (isAgeGated(storage)) return "under13";
  const band = ageBandFromBirth(month, year, now);
  if (band === "under13") storage.setItem(AGE_GATE_KEY, "under13");
  return band;
}

export function isAgeGated(storage: KeyValueStorage = browserStorage()): boolean {
  return storage.getItem(AGE_GATE_KEY) === "under13";
}

export interface PrivacyAck {
  materialVersion: number;
  acknowledgedAtMs: number;
  ageBand?: VoiceAgeBand;
}

const isVoiceBand = (v: unknown): v is VoiceAgeBand => v === "13-17" || v === "18+";

export function loadPrivacyAck(storage: KeyValueStorage = browserStorage()): PrivacyAck | null {
  const raw = readJson(storage, PRIVACY_KEY);
  if (!raw || typeof raw !== "object") return null;
  const { materialVersion, acknowledgedAtMs, ageBand } = raw as Record<string, unknown>;
  if (typeof materialVersion !== "number" || !Number.isFinite(materialVersion)) return null;
  if (typeof acknowledgedAtMs !== "number") return null;
  return isVoiceBand(ageBand) ? { materialVersion, acknowledgedAtMs, ageBand } : { materialVersion, acknowledgedAtMs };
}

/**
 * Records an acknowledgement. Like the server's, a later acknowledgement that
 * omits the age band keeps the one already held: the first-visit sheet does not
 * ask an age, and acknowledging it must not take voice away from somebody who
 * already answered.
 */
export function savePrivacyAck(
  ack: { materialVersion: number; ageBand?: VoiceAgeBand },
  storage: KeyValueStorage = browserStorage(),
  now = Date.now(),
): PrivacyAck {
  const previous = loadPrivacyAck(storage);
  const ageBand = ack.ageBand ?? previous?.ageBand;
  const record: PrivacyAck = { materialVersion: ack.materialVersion, acknowledgedAtMs: now, ...(ageBand ? { ageBand } : {}) };
  storage.setItem(PRIVACY_KEY, JSON.stringify(record));
  return record;
}

/**
 * The age band, when this device holds everything voice needs: an
 * acknowledgement of the current material version and an answer to the age
 * question. Null means the voice sheet has to be shown first.
 */
export function voiceReadyBand(ack: PrivacyAck | null, materialVersion: number): VoiceAgeBand | null {
  if (!ack || ack.materialVersion < materialVersion) return null;
  return ack.ageBand ?? null;
}

/**
 * Whether pressing Sign in with Google has to show the policy sheet first
 * (docs/VOICE_CHAT.md, "What acknowledgement gates").
 *
 * Voice's question without the age half: sign-in ties play to an email, which
 * is what the sheet is for, but it sends nothing to strangers, so an
 * acknowledgement of the current material version is the whole of it.
 *
 * `linkVisible` is `privacyLinkVisible()`. When the policy cannot be shown - a
 * prod build while it is still a draft - there is nothing to acknowledge, and
 * sign-in must go on working exactly as it did before the gate existed rather
 * than putting a draft in front of a production player.
 */
export function signInNeedsPolicy(ack: PrivacyAck | null, materialVersion: number, linkVisible: boolean): boolean {
  if (!linkVisible) return false;
  return !ack || ack.materialVersion < materialVersion;
}

/**
 * "Join voice automatically". Unset, it follows the age band: on for 18+, off
 * for 13-17, because auto-joining a room of strangers is exactly the default the
 * UK Children's Code asks services used by children not to have. Set, it is the
 * player's own choice either way.
 */
export function autoJoinEnabled(band: VoiceAgeBand | null | undefined, storage: KeyValueStorage = browserStorage()): boolean {
  const raw = storage.getItem(AUTO_JOIN_KEY);
  if (raw === "1") return true;
  if (raw === "0") return false;
  return band === "18+";
}

export function setAutoJoin(on: boolean, storage: KeyValueStorage = browserStorage()): void {
  storage.setItem(AUTO_JOIN_KEY, on ? "1" : "0");
}

function loadUnmutedMatches(storage: KeyValueStorage): string[] {
  const raw = readJson(storage, UNMUTED_MATCHES_KEY);
  return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === "string") : [];
}

/** Notes that the player unmuted in this match. Distinct lobby ids only. */
export function recordUnmutedMatch(lobbyId: string, storage: KeyValueStorage = browserStorage()): void {
  const seen = loadUnmutedMatches(storage);
  // Nothing past the threshold is ever read, so there is nothing to keep.
  if (seen.includes(lobbyId) || seen.length >= CALLOUT_RETIRES_AFTER) return;
  storage.setItem(UNMUTED_MATCHES_KEY, JSON.stringify([...seen, lobbyId]));
}

/**
 * Whether the "You're muted" callout has retired. By the third match a player
 * has found the button, and a hint that never retires becomes something people
 * learn to tap away blind; the mic button's muted icon stays as the permanent
 * indicator.
 */
export function mutedCalloutRetired(storage: KeyValueStorage = browserStorage()): boolean {
  return loadUnmutedMatches(storage).length >= CALLOUT_RETIRES_AFTER;
}

/**
 * The voice level, 0..1, defaulting to full when missing OR blank - for the
 * reason sound.ts's loadLevel spells out: `Number("")` is 0, and a cleared value
 * must read as the default, not as silence.
 */
export function getVoiceLevel(storage: KeyValueStorage = browserStorage()): number {
  const raw = storage.getItem(LEVEL_KEY);
  if (raw === null || raw.trim() === "") return 1;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 1;
  return Math.max(0, Math.min(1, value));
}

export function setVoiceLevel(value: number, storage: KeyValueStorage = browserStorage()): void {
  storage.setItem(LEVEL_KEY, String(Math.max(0, Math.min(1, value))));
}
